/**
 * 프로젝트 매출 귀속부서 배분(ProjectDepartmentAllocation) 계산 유틸
 *
 * 각 프로젝트의 매출은 매출 귀속부서의 배분율(allocation_rate)에 따라 분할된다.
 * 배분 정보가 없는 프로젝트는 대표 매출 귀속부서(primary_department_id) 100%로 간주한다.
 */

const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * 프로젝트의 매출 배분 목록을 정규화
 *
 * API에 따라 { department_id, allocation_rate, department: {...} } 또는
 * { department_id, department_name, department_code, allocation_rate } 형태로 내려오므로 둘 다 지원한다.
 */
export const getAllocations = (project) => {
    if (!project) return [];

    const active = (project.department_allocations || []).filter(
        alloc => alloc && alloc.is_active !== false
    );

    if (active.length > 0) {
        return active
            .map(alloc => ({
                department_id: alloc.department_id ?? alloc.department?.id,
                department_name: alloc.department_name ?? alloc.department?.name,
                department_code: alloc.department_code ?? alloc.department?.code,
                allocation_rate: toNumber(alloc.allocation_rate),
            }))
            .filter(alloc => alloc.department_id !== undefined && alloc.department_id !== null);
    }

    // 배분 정보가 없으면 대표 매출 귀속부서 100%
    const primaryId = project.primary_department_id ?? project.primary_department?.id;
    if (primaryId === undefined || primaryId === null) return [];

    return [{
        department_id: primaryId,
        department_name: project.primary_department?.name,
        department_code: project.primary_department?.code,
        allocation_rate: 100,
    }];
};

/**
 * 프로젝트에서 대상 부서들이 차지하는 배분율 합계 (%)
 * departmentIds를 생략하면 프로젝트 전체 배분율 합계를 반환한다.
 */
export const getAllocationRate = (project, departmentIds) => {
    const allocations = getAllocations(project);

    if (departmentIds === undefined || departmentIds === null) {
        return allocations.reduce((sum, alloc) => sum + alloc.allocation_rate, 0);
    }

    const targets = new Set(
        (Array.isArray(departmentIds) ? departmentIds : [departmentIds]).map(Number)
    );

    return allocations
        .filter(alloc => targets.has(Number(alloc.department_id)))
        .reduce((sum, alloc) => sum + alloc.allocation_rate, 0);
};

/** 대상 부서들에 해당하는 배분 목록만 반환 */
export const getAllocationsFor = (project, departmentIds) => {
    if (departmentIds === undefined || departmentIds === null) return getAllocations(project);

    const targets = new Set(
        (Array.isArray(departmentIds) ? departmentIds : [departmentIds]).map(Number)
    );

    return getAllocations(project).filter(alloc => targets.has(Number(alloc.department_id)));
};

/** 금액을 대상 부서들의 배분율로 안분한 금액 */
export const getAllocatedAmount = (amount, project, departmentIds) =>
    toNumber(amount) * getAllocationRate(project, departmentIds) / 100;

/** 계약금액 중 대상 부서들에 배분된 금액 */
export const getAllocatedContractAmount = (project, departmentIds) =>
    getAllocatedAmount(project?.contract_amount, project, departmentIds);

/**
 * 대상 부서들이 프로젝트의 배분 전체를 포함하는지 여부
 * false면 일부 부서만 선택된 "부분 배분" 상태다.
 */
export const isFullyAllocatedTo = (project, departmentIds) =>
    Math.abs(getAllocationRate(project) - getAllocationRate(project, departmentIds)) < 0.005;

/** 12,345,678원 */
export const formatAmount = (amount) => `${Math.round(toNumber(amount)).toLocaleString()}원`;

/** 30% / 33.33% (불필요한 소수점 제거) */
export const formatRate = (rate) => `${Number(toNumber(rate).toFixed(2))}%`;
